import {
  FormattedPlanDetails,
  LanguageNameSpaces,
  UsageType,
} from "@shared/types";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import { GetQuotaPlanFeatureType } from "@shared/features/settings/subscription/types";
import { sharedLib } from "@shared/services/shared.service";
import { useUserUsageTypes } from "@shared/utils/hooks/misc";
import { useCheckQuotaUsage } from "@shared/features/settings/hooks";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import { useMemo } from "react";
import pluralize from "pluralize";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface usePlanFeaturesProps {
  plan?: FormattedPlanDetails;
  getQuotaPlanFeature: GetQuotaPlanFeatureType;
}

const usePlanFeatures = ({
  plan,
  getQuotaPlanFeature,
}: usePlanFeaturesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { data: usageLimits } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery({
      is_demo_query: false,
    });

  const usageTypes = useUserUsageTypes();
  const { getExtraQuota } = useCheckQuotaUsage();

  const selectedPlanUsageLimits = useMemo(() => {
    if (!plan || !plan?.subscription_plan_type) return;
    return usageLimits?.limits?.[plan.subscription_plan_type];
  }, [plan, usageLimits]);

  const getPlanFeatures = () => {
    const baseFeatures = usageTypes.map((usageType) => {
      const limit = selectedPlanUsageLimits?.[usageType];
      const isUnlimited = limit === "inf";
      const numberLimit = isUnlimited || limit === true ? 2 : limit || 0;
      const showAdditionalQuota = !isUnlimited && !!getExtraQuota(usageType);
      const extraQuota = getExtraQuota(usageType);
      const titleObjectName = pluralize(
        t(getUsageTitleKey(usageType, plan?.subscription_plan_type)),
        numberLimit
      );
      const numItemsText = isUnlimited ? "Unlimited" : limit?.toString();
      return getQuotaPlanFeature(
        usageType,
        t,
        numItemsText,
        titleObjectName,
        showAdditionalQuota,
        extraQuota,
        plan?.subscription_plan_type
      );
    });
    if (isWhitelabelSubscriptionType(plan?.subscription_plan_type)) {
      return [
        ...baseFeatures,
        getQuotaPlanFeature(
          UsageType.SECURE_CLIENT_INVITE,
          t,
          undefined,
          undefined,
          undefined,
          undefined,
          plan?.subscription_plan_type
        ),
        getQuotaPlanFeature("invoicing", t),
        getQuotaPlanFeature("whitelabel", t),
      ];
    }
    return baseFeatures;
  };

  return { getPlanFeatures, selectedPlanUsageLimits };
};

export default usePlanFeatures;
